import { useColorScheme, useTheme } from '@mui/material/styles';

export interface UsageSeriesColors {
  /** The "actual usage" series — follows the customer's brand primary. */
  actualColor: string;
  /** The neutral "budget" reference line. Never branded (see below). */
  budgetColor: string;
}

/**
 * The actual-vs-budget series colors shared by every usage chart (both
 * dashboards, both report views, line detail, and `AreaTrendChart` itself).
 * Previously these two lines were copy-pasted into six files as literal hexes,
 * which meant they silently ignored per-customer branding.
 *
 * `actualColor` reads `palette.primary`, so it tracks the customer's
 * `BrandPrimaryColor` (WEB PRD §2.6). `budgetColor` stays neutral mode-derived
 * ink on purpose: it is the *reference* line, and if it also followed the brand
 * the two series could collapse into near-identical colors for customers whose
 * brand happens to be dark navy.
 *
 * <p>Returns concrete color strings, not `var(--mui-…)` references: these feed
 * SVG `fill`/`stroke` **presentation attributes**, where CSS custom properties
 * are not reliably resolved. `theme.palette.*` yields raw values here (the theme
 * enables `cssVariables`, but `useTheme()` still exposes resolved colors —
 * asserted in `__tests__/theme.test.tsx`).</p>
 */
export function useUsageSeriesColors(): UsageSeriesColors {
  const theme = useTheme();
  const { mode, systemMode } = useColorScheme();
  const isDark = (mode === 'system' ? systemMode : mode) === 'dark';

  return {
    actualColor: isDark ? theme.palette.primary.light : theme.palette.primary.main,
    budgetColor: isDark ? 'rgba(255,255,255,0.55)' : '#041D33',
  };
}
